import { ChevronDown } from 'lucide-react'
import { cn } from '../lib/cn.ts'

export interface SelectOption<T extends string> {
  value: T
  label: string
}

interface NativeSelectProps<T extends string> {
  value: T
  options: ReadonlyArray<SelectOption<T>>
  onChange: (value: T) => void
  /** Tints the control while it narrows the list, like an active chip. */
  highlighted?: boolean
  className?: string
}

/**
 * The phone's own picker instead of a row of chips: one 44px control, full list on tap.
 * 16px text keeps iOS Safari from zooming in on focus. Wrap it in a <label> to name it.
 */
export function NativeSelect<T extends string>({
  value,
  options,
  onChange,
  highlighted = false,
  className,
}: NativeSelectProps<T>) {
  return (
    <span className={cn('relative block min-w-0', className)}>
      <select
        value={value}
        onChange={(event) => {
          const option = options.find((item) => item.value === event.target.value)
          if (option) onChange(option.value)
        }}
        className={cn(
          'min-h-11 w-full appearance-none truncate rounded-control border py-2 pr-9 pl-3.5 text-[16px] outline-none',
          'transition-[border-color,box-shadow] focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-ring)]',
          highlighted ? 'border-[#bfd4c5] bg-brand-tint text-brand-ink' : 'border-line-strong bg-surface text-ink',
        )}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-ink-muted"
        aria-hidden
      />
    </span>
  )
}
